/**
 * SeancesLiveHistory.jsx — Historique des séances live récentes (enseignant).
 *
 * Route : /enseignant/seances-live
 *
 * Source de données : `listSeancesLive()` (localStorage), aucun backend.
 *
 * Affichage :
 *  - liste triée par date décroissante (updatedAt)
 *  - chaque carte = code REMC + intitulé + date + score % + passages
 *  - actions par carte : Reprendre la séance / Supprimer
 *  - empty state si rien
 *  - action globale : Effacer tout l'historique
 */
import React, { useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, Clock, Target, Trash2, ChevronRight,
  History as HistoryIcon, AlertCircle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import useSeo from '../hooks/useSeo';
import { listSeancesLive } from '../hooks/useSeanceLive';
import { getLivretItem } from '../data/teacher/evalInference';
import { getObjectifConcis } from '../data/teacher/objectifsConcis';

const STORAGE_KEY = 'c2r:seance-live:v1';

const CATEGORIE_COLORS = {
  C1: '#0891b2',
  C2: '#7c3aed',
  C3: '#dc2626',
  C4: '#16a34a',
};

function formatRelative(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const diffMin = Math.round(diffMs / 60000);
    if (diffMin < 1) return "à l'instant";
    if (diffMin < 60) return `il y a ${diffMin} min`;
    const diffH = Math.round(diffMin / 60);
    if (diffH < 24) return `il y a ${diffH} h`;
    const diffD = Math.round(diffH / 24);
    if (diffD < 7) return `il y a ${diffD} j`;
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

function computeStats(entry) {
  const statuts = Object.values(entry?.critereStatuts || {});
  const okCount = statuts.filter((s) => s === 'ok').length;
  const koCount = statuts.filter((s) => s === 'ko').length;
  const totalTested = okCount + koCount;
  const okRatio = totalTested > 0 ? Math.round((okCount / totalTested) * 100) : null;
  return { okCount, koCount, totalTested, okRatio };
}

function deleteEntry(code) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const all = JSON.parse(raw);
    delete all[code];
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all));
  } catch {
    /* ignore */
  }
}

function clearAll() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/* ── Carte séance ────────────────────────────────────────────────── */
function SeanceCard({ entry, onDelete }) {
  const livret = useMemo(() => getLivretItem(entry.code), [entry.code]);
  const concis = useMemo(() => getObjectifConcis(entry.code), [entry.code]);
  const stats = useMemo(() => computeStats(entry), [entry]);
  const categorie = entry.code ? entry.code.split('-')[0] : 'C1';
  const accent = CATEGORIE_COLORS[categorie] || '#0891b2';
  const hasContent =
    (entry.briefingNotes && entry.briefingNotes.trim()) ||
    (entry.guidageNotes && entry.guidageNotes.trim()) ||
    Object.keys(entry.critereStatuts || {}).length > 0 ||
    (entry.passages || 0) > 0;

  return (
    <article
      className="group rounded-2xl border-2 p-4 bg-card hover:shadow-lg transition-all"
      style={{ borderColor: `${accent}33` }}
      data-testid={`seance-card-${entry.code}`}
    >
      <div className="flex items-start gap-3">
        {/* Badge code REMC */}
        <div
          className="w-14 h-14 rounded-xl flex flex-col items-center justify-center text-white flex-shrink-0 font-black"
          style={{ background: accent }}
        >
          <span className="text-[9px] uppercase tracking-widest opacity-80 leading-none">REMC</span>
          <span className="text-base leading-none mt-0.5">{entry.code}</span>
        </div>

        {/* Contenu principal */}
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-black leading-tight mb-1 line-clamp-2" data-testid={`seance-intitule-${entry.code}`}>
            {livret?.intitule || `Compétence ${entry.code}`}
          </h3>
          {concis?.objectifCourt && (
            <p className="text-xs text-muted-foreground italic line-clamp-2 mb-2">
              « {concis.objectifCourt} »
            </p>
          )}

          {/* Stats inline */}
          <div className="flex flex-wrap gap-2 text-xs">
            <span
              className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-black"
              style={{ background: `${accent}15`, color: accent }}
            >
              <Clock className="w-3 h-3" /> {formatRelative(entry.updatedAt || entry.startedAt)}
            </span>
            {stats.okRatio != null && (
              <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-black bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">
                <Target className="w-3 h-3" /> {stats.okRatio}%
              </span>
            )}
            <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-black bg-muted text-muted-foreground">
              {entry.passages || 0} passages
            </span>
            <span className="inline-flex items-center gap-1 rounded-md px-2 py-0.5 font-black bg-muted text-muted-foreground">
              {stats.okCount}/{stats.totalTested || (concis?.criteresPrecis?.length || 0)} OK
            </span>
          </div>

          {!hasContent && (
            <p className="text-[11px] italic text-muted-foreground mt-2">
              Séance ouverte mais aucune saisie pour l’instant.
            </p>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border">
        <Button
          asChild
          size="sm"
          className="flex-1 text-white font-black gap-1.5"
          style={{ background: accent }}
          data-testid={`btn-resume-${entry.code}`}
        >
          <Link to={`/enseignant/seance-live/${entry.code}`}>
            Reprendre <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="gap-1.5 text-destructive hover:bg-destructive/10"
          onClick={() => onDelete(entry.code)}
          data-testid={`btn-delete-${entry.code}`}
          aria-label={`Supprimer la séance ${entry.code}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </article>
  );
}

/* ── Page ────────────────────────────────────────────────────────── */
export default function SeancesLiveHistory() {
  const [version, setVersion] = useState(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const seances = useMemo(() => listSeancesLive(), [version]);

  useSeo({
    title: 'Séances Live récentes — Espace enseignant',
    description: 'Historique des séances live menées sur le terrain : reprends, supprime ou consulte tes séances précédentes.',
    path: '/enseignant/seances-live',
  });

  const handleDelete = useCallback((code) => {
    if (!window.confirm(`Supprimer la séance "${code}" ? Les notes seront perdues.`)) return;
    deleteEntry(code);
    setVersion((v) => v + 1);
    toast.success('Séance supprimée', { description: code });
  }, []);

  const handleClearAll = useCallback(() => {
    if (!window.confirm('Effacer toutes les séances live de cet appareil ? Cette action est irréversible.')) return;
    clearAll();
    setVersion((v) => v + 1);
    toast.success('Historique effacé');
  }, []);

  return (
    <div className="min-h-screen pb-12" data-testid="seances-live-history-page">
      <div className="max-w-3xl mx-auto px-4 pt-4">
        {/* Top bar */}
        <div className="flex items-center justify-between gap-3 mb-4">
          <Button asChild variant="ghost" size="sm" className="gap-1" data-testid="btn-back">
            <Link to="/enseignant">
              <ArrowLeft className="w-4 h-4" /> Espace enseignant
            </Link>
          </Button>
          <span className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">
            Historique
          </span>
        </div>

        {/* Header */}
        <header className="mb-6">
          <div className="flex items-start gap-3">
            <div
              className="w-12 h-12 rounded-2xl flex items-center justify-center text-white flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)' }}
            >
              <HistoryIcon className="w-6 h-6" />
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-black leading-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Séances Live récentes
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {seances.length === 0
                  ? "Aucune séance n'a encore été lancée sur cet appareil."
                  : `${seances.length} séance${seances.length > 1 ? 's' : ''} sauvegardée${seances.length > 1 ? 's' : ''} localement.`}
              </p>
            </div>
          </div>
        </header>

        {/* Liste ou empty state */}
        {seances.length === 0 ? (
          <div
            className="rounded-2xl border-2 border-dashed border-border p-8 text-center"
            data-testid="empty-state"
          >
            <div className="w-14 h-14 mx-auto rounded-2xl bg-muted flex items-center justify-center mb-3">
              <AlertCircle className="w-7 h-7 text-muted-foreground" />
            </div>
            <h2 className="text-lg font-black mb-1">Aucune séance pour l’instant</h2>
            <p className="text-sm text-muted-foreground mb-4 max-w-md mx-auto">
              Lance une séance live depuis l’évaluation Plateau ou Circulation : tes briefings,
              guidages et débriefs seront retrouvés ici.
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <Button asChild className="gap-2" data-testid="btn-go-plateau">
                <Link to="/enseignant/eval-plateau">
                  <BookOpen className="w-4 h-4" /> Évaluation Plateau
                </Link>
              </Button>
              <Button asChild variant="outline" className="gap-2" data-testid="btn-go-circulation">
                <Link to="/enseignant/eval-circulation">
                  <BookOpen className="w-4 h-4" /> Évaluation Circulation
                </Link>
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="space-y-3" data-testid="seances-list">
              {seances.map((entry) => (
                <SeanceCard key={entry.code} entry={entry} onDelete={handleDelete} />
              ))}
            </div>

            {/* Action globale */}
            <div className="flex justify-center pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={handleClearAll}
                className="gap-2 text-destructive hover:bg-destructive/10"
                data-testid="btn-clear-all"
              >
                <Trash2 className="w-4 h-4" /> Effacer tout l’historique
              </Button>
            </div>
          </>
        )}

        <p className="text-[11px] italic text-muted-foreground text-center pt-4">
          💾 Les séances sont stockées localement sur cet appareil. Pour les retrouver ailleurs,
          utilise le partage QR depuis le Débrief.
        </p>
      </div>
    </div>
  );
}
